// Draws a blade finish into a 512x512 canvas. The canvas covers the blade's side: base at the upper
// left, hooked tip at the lower right (the procedural blade's shape coords and the karambit model's
// projected UVs both map that way).

import { FINISHES, type Finish, type FinishDef } from './finishes';

const SIZE = 512;

function rng(seed: number) {
  let s = seed;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

/** Blade-wise gradient (base upper left to tip lower right). */
function along(g: CanvasRenderingContext2D, stops: readonly string[]) {
  const gr = g.createLinearGradient(20, 150, 490, 480);
  stops.forEach((c, i) => gr.addColorStop(i / Math.max(1, stops.length - 1), c));
  return gr;
}

function marble(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  g.fillStyle = along(g, f.colors);
  g.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 70; i++) {
    const x = rnd() * SIZE;
    const y = rnd() * SIZE;
    const warm = x + y * 0.8 < 460;
    g.strokeStyle =
      rnd() < 0.5
        ? `rgba(255,255,255,${0.08 + rnd() * 0.18})`
        : `rgba(${warm ? f.warm : f.cool},${0.1 + rnd() * 0.2})`;
    g.lineWidth = 1 + rnd() * 7;
    g.beginPath();
    g.moveTo(x, y);
    g.bezierCurveTo(
      x + (rnd() - 0.3) * 160,
      y + (rnd() - 0.5) * 120,
      x + (rnd() - 0.2) * 220,
      y + (rnd() - 0.5) * 160,
      x + 120 + rnd() * 160,
      y + (rnd() - 0.5) * 140,
    );
    g.stroke();
  }
}

function blob(g: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha: number) {
  const gr = g.createRadialGradient(x, y, 0, x, y, r);
  g.globalAlpha = alpha;
  gr.addColorStop(0, color);
  gr.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = gr;
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.globalAlpha = 1;
}

function doppler(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, ...hi] = f.colors;
  g.fillStyle = base;
  g.fillRect(0, 0, SIZE, SIZE);
  // Big soft clouds, then smaller bright swirls on top.
  for (let i = 0; i < 26; i++)
    blob(g, rnd() * SIZE, rnd() * SIZE, 60 + rnd() * 130, hi[i % hi.length], 0.35 + rnd() * 0.35);
  g.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 40; i++) {
    const x = rnd() * SIZE;
    const y = rnd() * SIZE;
    g.strokeStyle = hi[Math.floor(rnd() * hi.length)];
    g.globalAlpha = 0.12 + rnd() * 0.2;
    g.lineWidth = 6 + rnd() * 20;
    g.beginPath();
    g.arc(x, y, 20 + rnd() * 70, rnd() * 6, rnd() * 6 + 1.5 + rnd() * 2);
    g.stroke();
  }
  g.globalAlpha = 1;
  g.globalCompositeOperation = 'source-over';
  // Dark pockets keep the polished, deep look.
  for (let i = 0; i < 10; i++) blob(g, rnd() * SIZE, rnd() * SIZE, 40 + rnd() * 70, base, 0.5);
}

function fade(g: CanvasRenderingContext2D, f: FinishDef) {
  g.fillStyle = along(g, f.colors);
  g.fillRect(0, 0, SIZE, SIZE);
  // A soft sheen across the middle.
  const sh = g.createLinearGradient(0, 0, SIZE, 0);
  sh.addColorStop(0, 'rgba(255,255,255,0)');
  sh.addColorStop(0.5, 'rgba(255,255,255,0.12)');
  sh.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = sh;
  g.fillRect(0, 0, SIZE, SIZE);
}

function tiger(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, light, stripe] = f.colors;
  g.fillStyle = along(g, [light, base, light, base]);
  g.fillRect(0, 0, SIZE, SIZE);
  g.fillStyle = stripe;
  // Tapered brush strokes across the blade.
  for (let i = 0; i < 22; i++) {
    const x = -40 + i * 26 + rnd() * 12;
    const w = 6 + rnd() * 12;
    const bend = (rnd() - 0.5) * 80;
    g.beginPath();
    g.moveTo(x, -10);
    g.quadraticCurveTo(x + bend + w, SIZE * 0.5, x + 40 + w * 0.3, SIZE + 10);
    g.quadraticCurveTo(x + bend, SIZE * 0.5, x - w * 0.4, -10);
    g.fill();
  }
}

function web(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, line] = f.colors;
  g.fillStyle = base;
  g.fillRect(0, 0, SIZE, SIZE);
  g.strokeStyle = line;
  g.lineCap = 'round';
  // Two webs: spokes from a centre, sagging rings between them.
  for (const [cx, cy] of [
    [150 + rnd() * 60, 180 + rnd() * 60],
    [380 + rnd() * 60, 380 + rnd() * 60],
  ]) {
    const spokes = 11;
    const angles = Array.from({ length: spokes }, (_, i) => (i / spokes) * Math.PI * 2 + rnd() * 0.3);
    g.lineWidth = 3;
    for (const a of angles) {
      g.beginPath();
      g.moveTo(cx, cy);
      g.lineTo(cx + Math.cos(a) * 420, cy + Math.sin(a) * 420);
      g.stroke();
    }
    g.lineWidth = 2;
    for (let r = 28; r < 420; r += 26 + rnd() * 10) {
      g.beginPath();
      for (let i = 0; i <= spokes; i++) {
        const a0 = angles[i % spokes];
        const a1 = angles[(i + 1) % spokes] + (i + 1 === spokes ? Math.PI * 2 : 0);
        const mid = (a0 + a1) / 2;
        const x0 = cx + Math.cos(a0) * r;
        const y0 = cy + Math.sin(a0) * r;
        if (i === 0) g.moveTo(x0, y0);
        if (i < spokes)
          g.quadraticCurveTo(
            cx + Math.cos(mid) * r * 0.86,
            cy + Math.sin(mid) * r * 0.86,
            cx + Math.cos(a1) * r,
            cy + Math.sin(a1) * r,
          );
      }
      g.stroke();
    }
  }
}

function patches(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, ...hi] = f.colors;
  g.fillStyle = along(g, [base, '#b8bec6', base]);
  g.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 30; i++)
    blob(g, rnd() * SIZE, rnd() * SIZE, 40 + rnd() * 110, hi[i % hi.length], 0.45 + rnd() * 0.4);
}

function damascus(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, light, dark] = f.colors;
  g.fillStyle = base;
  g.fillRect(0, 0, SIZE, SIZE);
  const ph = [rnd() * 6, rnd() * 6, rnd() * 6];
  for (let row = -20; row < SIZE + 40; row += 7) {
    g.strokeStyle = row % 14 ? light : dark;
    g.globalAlpha = 0.55;
    g.lineWidth = 3;
    g.beginPath();
    for (let x = 0; x <= SIZE; x += 8) {
      const y =
        row +
        Math.sin(x * 0.021 + ph[0]) * 14 +
        Math.sin(x * 0.053 + row * 0.05 + ph[1]) * 7 +
        Math.sin(x * 0.11 + ph[2]) * 3;
      if (x === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  }
  g.globalAlpha = 1;
}

function solid(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  g.fillStyle = along(g, [f.colors[0], f.colors[1] ?? f.colors[0], f.colors[0]]);
  g.fillRect(0, 0, SIZE, SIZE);
  // Brushed grain along the blade.
  for (let i = 0; i < 260; i++) {
    const y = rnd() * SIZE;
    g.strokeStyle = rnd() < 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(SIZE, y + (rnd() - 0.5) * 20);
    g.stroke();
  }
}

function rust(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, light, dark] = f.colors;
  g.fillStyle = base;
  g.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 40; i++)
    blob(g, rnd() * SIZE, rnd() * SIZE, 20 + rnd() * 90, i % 2 ? light : dark, 0.5);
  for (let i = 0; i < 2500; i++) {
    g.fillStyle = rnd() < 0.5 ? light : dark;
    g.globalAlpha = 0.3 + rnd() * 0.4;
    const s = 1 + rnd() * 3;
    g.fillRect(rnd() * SIZE, rnd() * SIZE, s, s);
  }
  g.globalAlpha = 1;
}

function butcher(g: CanvasRenderingContext2D, f: FinishDef, rnd: () => number) {
  const [base, mid, pale] = f.colors;
  g.fillStyle = base;
  g.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 26; i++) blob(g, rnd() * SIZE, rnd() * SIZE, 30 + rnd() * 80, mid, 0.6);
  for (let i = 0; i < 18; i++) blob(g, rnd() * SIZE, rnd() * SIZE, 15 + rnd() * 45, pale, 0.55);
}

/** Paint `finish` into a new canvas. */
export function drawFinish(finish: Finish): HTMLCanvasElement {
  const f: FinishDef = FINISHES[finish] ?? FINISHES.fireice;
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const g = c.getContext('2d')!;
  // Same seed per finish: a finish always looks the same.
  let seed = 7;
  for (const ch of finish) seed = (seed * 31 + ch.charCodeAt(0)) % 2147483646;
  const rnd = rng(seed + 1);
  switch (f.pattern) {
    case 'marble':
      marble(g, f, rnd);
      break;
    case 'doppler':
      doppler(g, f, rnd);
      break;
    case 'fade':
      fade(g, f);
      break;
    case 'tiger':
      tiger(g, f, rnd);
      break;
    case 'web':
      web(g, f, rnd);
      break;
    case 'patches':
      patches(g, f, rnd);
      break;
    case 'damascus':
      damascus(g, f, rnd);
      break;
    case 'solid':
      solid(g, f, rnd);
      break;
    case 'rust':
      rust(g, f, rnd);
      break;
    case 'butcher':
      butcher(g, f, rnd);
      break;
  }
  return c;
}
